import { OrderedSet, fromJS } from 'immutable'
import { createSelector } from 'reselect'
import snakeCase from 'lodash/snakeCase'
import upperFirst from 'lodash/upperFirst'
import { createCRUDActions } from '../redux/createCRUDActions'
import { concatActionReducers } from '../redux/concatActionReducers'
import {
  addPrefixToActionTypeKey,
  addPrefixToActionTypeName,
  getSortFuncForSelector,
  getListFuncForSelector,
} from './utils'

const defaultProps = {
  sortable: false,
  orderable: false,
  paginated: false,
  perPage: 10,
  filterable: true,
  filterField: 'name',
  pending: ['getAll', 'get', 'post', 'patch', 'delete'],
  loaded: ['getAll'],
  additionalInitialState: {},
}

class Entity {
  constructor(inputProps) {
    this.options = {
      ...defaultProps,
      ...inputProps,
    }
    if (typeof inputProps.name !== 'string' || inputProps.name === '') {
      throw new Error('Entity should have name')
    }
    this.name = inputProps.name
    this.actionPostfixes = {
      SORT_BY: 'SORT_BY',
      SELECT_PAGE: 'SELECT_PAGE',
      SET_PENDING: 'SET_PENDING',
      SET_ORDER: 'SET_ORDER',
      SET_LOADED: 'SET_LOADED',
      SET_FILTER: 'SET_FILTER',
      SET_VIEW: 'SET_VIEW',
    }

    this.generateInitialState()
    this.generateActionTypes()
    this.generateActions()
    this.generateSelectors()
    this.generateReducer()
  }

  getActionTypeKey() {
    return addPrefixToActionTypeKey(this.name)
  }

  getActionTypeName() {
    return addPrefixToActionTypeName(this.name)
  }

  getSelectors() {
    return this.selectors
  }

  getActions() {
    return this.actions
  }

  getActionTypes() {
    return this.actionTypes
  }

  getReducer() {
    return this.reducer
  }

  getInitialState() {
    return this.initialState
  }

  addAction(actionName, actionType, action) {
    this.actions[actionName] = action
    const actionKey = snakeCase(actionType).toUpperCase()
    this.actionTypes[actionKey] = actionType
  }

  addActions(actions) {
    actions.forEach(({ name, type, action }) => {
      this.addAction(name, type, action)
    })
  }

  addSelector(selectorName, selectorCallback) {
    this.selectors[selectorName] = selectorCallback(this.selectors)
  }

  generateInitialState() {
    const { options } = this
    if (options.initialState !== undefined && options.initialState !== null) {
      this.initialState = fromJS(options.initialState)
      return
    }
    const initialState = {
      all: {},
      view: null,
      ids: OrderedSet(),
    }
    if (options.paginated) {
      if (typeof options.perPage === 'number' && options.perPage > 0) {
        initialState.pagination = {
          current: 0,
          perPage: options.perPage,
          count: 0,
        }
      } else {
        throw new Error('perPage should be number and greater then 0')
      }
    }
    if (options.sortable) {
      initialState.sort = {
        by: null,
        desc: false,
      }
    }
    if (options.filterable) {
      initialState.filter = ''
    }
    initialState.pending =
      (Array.isArray(options.pending) &&
        options.pending.reduce((acc, item) => {
          acc[item] = false
          return acc
        }, {})) ||
      {}
    initialState.loaded =
      (Array.isArray(options.loaded) &&
        options.loaded.reduce((acc, item) => {
          acc[item] = false
          return acc
        }, {})) ||
      {}
    this.initialState = fromJS({
      ...initialState,
      ...options.additionalInitialState,
    })
  }

  generateActionTypes() {
    const getATK = this.getActionTypeKey()
    const getATN = this.getActionTypeName()
    const { options } = this
    const actionTypes = {}
    actionTypes[getATK(this.actionPostfixes.SET_VIEW)] = getATN(this.actionPostfixes.SET_VIEW)
    if (options.sortable) {
      actionTypes[getATK(this.actionPostfixes.SORT_BY)] = getATN(this.actionPostfixes.SORT_BY)
    }
    if (options.paginated) {
      actionTypes[getATK(this.actionPostfixes.SELECT_PAGE)] = getATN(this.actionPostfixes.SELECT_PAGE)
    }
    if (Array.isArray(options.pending)) {
      const { actionTypes: crudTypes } = createCRUDActions(this.name, options.pending)
      actionTypes[snakeCase(this.name).toUpperCase()] = crudTypes
      actionTypes[getATK(this.actionPostfixes.SET_PENDING)] = getATN(this.actionPostfixes.SET_PENDING)
    }
    if (Array.isArray(options.loaded)) {
      actionTypes[getATK(this.actionPostfixes.SET_LOADED)] = getATN(this.actionPostfixes.SET_LOADED)
    }
    if (options.orderable) {
      actionTypes[getATK(this.actionPostfixes.SET_ORDER)] = getATN(this.actionPostfixes.SET_ORDER)
    }
    if (options.filterable) {
      actionTypes[getATK(this.actionPostfixes.SET_FILTER)] = getATN(this.actionPostfixes.SET_FILTER)
    }
    this.actionTypes = actionTypes
  }

  generateActions() {
    const { options } = this
    const actionTypes = this.getActionTypes()
    const getATK = this.getActionTypeKey()
    const actions = {}
    actions.setView = id => ({
      type: actionTypes[getATK(this.actionPostfixes.SET_VIEW)],
      payload: {
        id,
      },
    })
    if (options.sortable) {
      actions.sortBy = (by, desc) => ({
        type: actionTypes[getATK(this.actionPostfixes.SORT_BY)],
        payload: {
          by,
          desc,
        },
      })
    }
    if (options.paginated) {
      actions.selectPage = page => ({
        type: actionTypes[getATK(this.actionPostfixes.SELECT_PAGE)],
        payload: {
          page,
        },
      })
    }
    if (options.orderable) {
      actions.setOrder = (list, oldIndex, newIndex) => ({
        type: actionTypes[getATK(this.actionPostfixes.SET_ORDER)],
        payload: {
          list,
          oldIndex,
          newIndex,
        },
      })
    }
    if (options.filterable) {
      actions.filter = filter => ({
        type: actionTypes[getATK(this.actionPostfixes.SET_FILTER)],
        payload: {
          filter,
        },
      })
    }
    if (Array.isArray(options.loaded)) {
      actions.setLoaded = (field, loaded) => ({
        type: actionTypes[getATK(this.actionPostfixes.SET_LOADED)],
        payload: {
          field,
          loaded,
        },
      })
    }
    if (Array.isArray(options.pending)) {
      actions[this.name] = createCRUDActions(this.name, options.pending).actions
      actions.setPending = (field, pending) => ({
        type: actionTypes[getATK(this.actionPostfixes.SET_PENDING)],
        payload: {
          field,
          pending,
        },
      })
    }
    this.actions = actions
  }

  generateSelectors() {
    const selectors = {}
    const { options } = this
    const start = (selectors.get = state => state.get(this.name))
    const all = `getAll`
    const view = `getView`
    const presenter = `getViewPresenter`
    const ids = `getIds`
    const size = `getSize`
    const pending = `getPending`
    const loaded = `getLoaded`
    const pagination = `getPagination`
    const list = 'getList'

    // SORTED
    const sort = `getSort`
    const sortBy = `getSortBy`
    const sortDesc = `getSortDesc`
    const sortedIds = `getSortedIds`
    const sortedList = `getSortedList`

    // FILTERABLE
    const filter = `getFilter`
    const filteredIdsSize = `getFilteredIdsSize`
    const filteredAll = `getFilteredAll`
    const filteredIds = `getFilteredIds`
    const filteredList = `getFilteredList`

    // SORTED FILTERABLE
    const sortedFilteredIds = `getSortedFilteredIds`
    const sortedFilteredList = `getSortedFilteredList`

    // FILTERABLE PAGINATED
    const filteredPagesCount = `getFilteredPagesCount`
    const filteredIdsForCurrentPage = `getFilteredIdsForCurrentPage`
    const filteredListForCurrentPage = `getFilteredListForCurrentPage`

    // PAGINATED
    const paginationCurrent = `getPaginationCurrent`
    const paginationPerPage = `getPaginationPerPage`
    const pageCount = `getPageCount`
    const idsForCurrentPage = `getIdsForCurrentPage`
    const listForCurrentPage = `getListForCurrentPage`

    // SORTED PAGINATED
    const sortedIdsForCurrentPage = `getSortedIdsForCurrentPage`
    const sortedListForCurrentPage = `getSortedListForCurrentPage`

    // SORTED FILTERABLE PAGINATED
    const sortedFilteredPagesCount = `getSortedFilteredPagesCount`
    const sortedFilteredIdsForCurrentPage = `getSortedFilteredIdsForCurrentPage`
    const sortedFilteredListForCurrentPage = `getSortedFilteredListForCurrentPage`

    // common selectors

    selectors[all] = createSelector(start, entity => entity.get('all'))
    selectors[view] = createSelector(start, entity => entity.get('view'))
    selectors[presenter] = createSelector(selectors[all], selectors[view], (all, view) =>
      view !== null ? (all.get(view.toString()) === undefined ? null : all.get(view.toString())) : null
    )
    selectors[ids] = createSelector(start, entity => entity.get('ids'))
    selectors[size] = createSelector(selectors[ids], ids => ids.size)
    selectors[list] = createSelector(selectors[all], selectors[ids], getListFuncForSelector)

    if (Array.isArray(options.pending)) {
      selectors[pending] = createSelector(start, entity => entity.get('pending'))
      // stupid check for flow
      options.pending !== undefined &&
        options.pending !== null &&
        options.pending.forEach(item => {
          selectors[`getPending${upperFirst(item)}`] = createSelector(selectors[pending], pending => pending.get(item))
        })
    }
    if (Array.isArray(options.loaded)) {
      selectors[loaded] = createSelector(start, entity => entity.get('loaded'))
      // stupid check for flow
      options.loaded !== undefined &&
        options.loaded !== null &&
        options.loaded.forEach(item => {
          selectors[`getLoaded${upperFirst(item)}`] = createSelector(selectors[loaded], loaded => loaded.get(item))
        })
    }

    // sorted
    selectors[sort] = createSelector(start, entity => entity.get('sort'))
    selectors[sortBy] = createSelector(selectors[sort], sort => sort.get('by'))
    selectors[sortDesc] = createSelector(selectors[sort], sort => sort.get('desc'))
    selectors[sortedIds] = createSelector(
      selectors[all],
      selectors[ids],
      selectors[sortBy],
      selectors[sortDesc],
      getSortFuncForSelector(options.sortFields)
    )
    selectors[sortedList] = createSelector(selectors[all], selectors[sortedIds], getListFuncForSelector)

    // FILTERABLE
    if (options.filterable && !options.filterField) {
      throw new Error('Provide filter field to options')
    }
    selectors[filter] = createSelector(start, entity => entity.get('filter'))
    selectors[filteredAll] = createSelector(selectors[all], selectors[filter], (all, filter) =>
      all.filter(value => value.get(options.filterField).toString().search(new RegExp(filter, 'i')) !== -1)
    )
    selectors[filteredIds] = createSelector(selectors[filteredAll], selectors[ids], (filteredAll, ids) =>
      ids.intersect(
        filteredAll
          .keySeq()
          .toArray()
          .map(id => parseInt(id))
      )
    )
    selectors[filteredList] = createSelector(selectors[all], selectors[filteredIds], getListFuncForSelector)
    selectors[filteredIdsSize] = createSelector(selectors[filteredIds], ids => ids.size)

    // PAGINATED
    selectors[pagination] = createSelector(start, entity => entity.get('pagination'))
    selectors[paginationCurrent] = createSelector(selectors[pagination], pagination => pagination.get('current'))
    selectors[paginationPerPage] = createSelector(selectors[pagination], pagination => pagination.get('perPage'))
    selectors[pageCount] = createSelector(selectors[size], selectors[paginationPerPage], (size, perPage) =>
      Math.ceil(size / perPage)
    )
    selectors[idsForCurrentPage] = createSelector(
      selectors[ids],
      selectors[paginationCurrent],
      selectors[paginationPerPage],
      (ids, currentPage, perPage) => ids.skip(currentPage * perPage).take(perPage)
    )
    selectors[listForCurrentPage] = createSelector(selectors[all], selectors[idsForCurrentPage], getListFuncForSelector)

    // SORTED FILTERABLE
    selectors[sortedFilteredIds] = createSelector(
      selectors[filteredAll],
      selectors[filteredIds],
      selectors[sortBy],
      selectors[sortDesc],
      getSortFuncForSelector(options.sortFields)
    )
    selectors[sortedFilteredList] = createSelector(selectors[all], selectors[sortedFilteredIds], getListFuncForSelector)

    // SORTABLE PAGINATED
    selectors[sortedIdsForCurrentPage] = createSelector(
      selectors[sortedIds],
      selectors[paginationCurrent],
      selectors[paginationPerPage],
      (ids, currentPage, perPage) => ids.skip(currentPage * perPage).take(perPage)
    )
    selectors[sortedListForCurrentPage] = createSelector(
      selectors[all],
      selectors[sortedIdsForCurrentPage],
      getListFuncForSelector
    )

    // FILTERABLE PAGINATED
    selectors[filteredPagesCount] = createSelector(
      selectors[filteredIdsSize],
      selectors[paginationPerPage],
      (size, perPage) => Math.ceil(size / perPage)
    )
    selectors[filteredIdsForCurrentPage] = createSelector(
      selectors[filteredIds],
      selectors[paginationCurrent],
      selectors[paginationPerPage],
      (ids, currentPage, perPage) => ids.skip(currentPage * perPage).take(perPage)
    )
    selectors[filteredListForCurrentPage] = createSelector(
      selectors[all],
      selectors[filteredIdsForCurrentPage],
      getListFuncForSelector
    )

    // SORTED FILTERABLE PAGINATED
    selectors[sortedFilteredPagesCount] = createSelector(
      selectors[filteredIdsSize],
      selectors[paginationPerPage],
      (size, perPage) => Math.ceil(size / perPage)
    )
    selectors[sortedFilteredIdsForCurrentPage] = createSelector(
      selectors[sortedFilteredIds],
      selectors[paginationCurrent],
      selectors[paginationPerPage],
      (ids, currentPage, perPage) => ids.skip(currentPage * perPage).take(perPage)
    )
    selectors[sortedFilteredListForCurrentPage] = createSelector(
      selectors[all],
      selectors[sortedFilteredIdsForCurrentPage],
      getListFuncForSelector
    )

    this.selectors = selectors
  }

  generateReducers() {
    const { options } = this
    const getATK = this.getActionTypeKey()
    const actionTypes = this.getActionTypes()
    const reducers = {}
    reducers[actionTypes[getATK(this.actionPostfixes.SET_VIEW)]] = (state, { payload }) => state.set('view', payload.id)
    if (options.sortable) {
      reducers[actionTypes[getATK(this.actionPostfixes.SORT_BY)]] = (state, { payload }) =>
        state.setIn(['sort', 'by'], payload.by).setIn(['sort', 'desc'], payload.desc)
    }
    if (options.paginated) {
      reducers[actionTypes[getATK(this.actionPostfixes.SELECT_PAGE)]] = (state, { payload }) =>
        state.setIn(['pagination', 'current'], payload.page)
    }
    if (options.filterable) {
      reducers[actionTypes[getATK(this.actionPostfixes.SET_FILTER)]] = (state, { payload }) =>
        state.set('filter', payload.filter)
    }
    if (Array.isArray(options.loaded)) {
      reducers[actionTypes[getATK(this.actionPostfixes.SET_LOADED)]] = (state, { payload }) =>
        state.setIn(['loaded', payload.field], payload.loaded)
    }
    if (Array.isArray(options.pending)) {
      reducers[actionTypes[getATK(this.actionPostfixes.SET_PENDING)]] = (state, { payload }) =>
        state.setIn(['pending', payload.field], payload.pending)
    }
    this.reducers = reducers
  }

  generateReducer() {
    if (!this.reducers) {
      this.generateReducers()
    }
    this.reducer = concatActionReducers(this.reducers, this.getInitialState())
  }

  addActionReducer(actionType, actionReducer) {
    this.reducers[actionType] = actionReducer
    this.generateReducer()
  }

  addActionReducers(actionReducers) {
    actionReducers.map(({ type, reducer }) => {
      this.addActionReducer(type, reducer)
    })
  }
}

export default Entity
